import { useGetComplianceInfo, useGetMetaDataCoporate } from "api/compliance";
import { useGetInvestorProfile, useGetMetaData } from "api/investor/queries";
import { useGetListProgressCorporate } from "api/investorRegistrationCoporate";
import { INVESTOR_PROFILE_QUERY_KEY, useGetProfileInvestor } from "api/user/queries";
import { useAppSelector } from "hooks/reduxHook";
import {
	createContext,
	createElement,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useQueryCache } from "react-query";
import { getUser } from "store/ducks/investor/slice";
import {
	isFullyVerifiedCorporateInvestor,
	isCorporateInvestorUser,
	needsCorporateClassificationUpdate,
} from "utils/corporateInvestorClassification";
import {
	IndividualInvestorClassificationsMap,
	isFullyVerifiedIndividualInvestor,
	isIndividualInvestorUser,
	needsIndividualClassificationUpdate,
} from "utils/individualInvestorClassification";
import { CorporateInvestorClassificationsMap } from "pages/InvestorPage/Corporate/CompanyInfo/components/Declaration/utils/investorClassification";
import {
	handleDataSelect,
	handleDataSelectWithout,
} from "pages/InvestorPage/Corporate/CompanyInfo/components/Declaration/utils/common";

export type InvestorClassificationAccountType = "individual" | "corporate";

function useInvestorClassificationRequirementState() {
	const queryCache = useQueryCache();
	const [isOpen, setIsOpen] = useState(false);
	const [sessionDismissed, setSessionDismissed] = useState(false);
	const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

	// Redux user updates on login/logout — ProfileProvider mounts before login, so
	// localStorage-only reads would not re-enable queries until a full reload.
	const userData = useAppSelector(getUser);
	const isLoggedIn = Boolean(userData?.id);
	const permissions: string[] =
		(userData as { permissions?: string[] } | null)?.permissions ?? [];

	// isIndividualInvestorUser([]) is true (negative permission check) — always
	// require a real session so guest/public pages never trigger these queries.
	const isIndividualInvestor = isLoggedIn && isIndividualInvestorUser(permissions);
	const isCorporateInvestor = isLoggedIn && isCorporateInvestorUser(permissions);
	const accountType: InvestorClassificationAccountType | null = isCorporateInvestor
		? "corporate"
		: isIndividualInvestor
			? "individual"
			: null;

	const { data: investorUserProfile, isLoading: isLoadingInvestorUserProfile } =
		useGetProfileInvestor(undefined, {
			enabled: isLoggedIn && (isIndividualInvestor || isCorporateInvestor),
			refetchOnWindowFocus: false,
		});

	const { data: corporateProgress, isLoading: isLoadingCorporateProgress } =
		useGetListProgressCorporate({
			enabled: isLoggedIn && isCorporateInvestor,
			refetchOnWindowFocus: false,
		});

	const { data: investorProfile, isLoading: isLoadingIndividualProfile } = useGetInvestorProfile({
		enabled: isLoggedIn && isIndividualInvestor,
		refetchOnWindowFocus: false,
	});

	const { data: complianceInfo, isLoading: isLoadingComplianceInfo } = useGetComplianceInfo(
		undefined,
		{
			enabled: isLoggedIn && isCorporateInvestor,
			refetchOnWindowFocus: false,
		},
	);

	const { data: individualMetadata, isLoading: isLoadingIndividualMetadata } = useGetMetaData({
		enabled: isLoggedIn && isIndividualInvestor,
		refetchOnWindowFocus: false,
	});

	const { data: corporateMetadata, isLoading: isLoadingCorporateMetadata } =
		useGetMetaDataCoporate(undefined, {
			enabled: isLoggedIn && isCorporateInvestor,
			refetchOnWindowFocus: false,
		});

	const isFullyVerifiedIndividual = isFullyVerifiedIndividualInvestor(
		investorUserProfile?.profileComplete,
	);
	const isFullyVerifiedCorporate = isFullyVerifiedCorporateInvestor(
		investorUserProfile?.profileComplete,
		corporateProgress,
	);

	const needsIndividualUpdate = needsIndividualClassificationUpdate(investorProfile);
	const needsCorporateUpdate = needsCorporateClassificationUpdate(complianceInfo);

	const shouldRequireUpdate =
		(isIndividualInvestor && isFullyVerifiedIndividual && needsIndividualUpdate) ||
		(isCorporateInvestor && isFullyVerifiedCorporate && needsCorporateUpdate);

	const individualInvestorClassifications =
		(individualMetadata?.individualInvestorClassifications as IndividualInvestorClassificationsMap) ??
		{};

	const corporateInvestorClassifications =
		(corporateMetadata?.corporateInvestorClassifications as CorporateInvestorClassificationsMap) ??
		{};

	const corporateMetadataOptions = useMemo(
		() => ({
			sourceOfFundsWealth: handleDataSelectWithout(
				corporateMetadata?.sourceOfFundsWealth ?? [],
			),
			investmentObjective: handleDataSelectWithout(
				corporateMetadata?.investmentObjective ?? [],
			),
			annualRevenue: handleDataSelect(corporateMetadata?.annualRevenue ?? []),
			investmentDuration: handleDataSelect(corporateMetadata?.investmentDuration ?? []),
			totalNetAssets: handleDataSelect(corporateMetadata?.totalNetAssets ?? []),
			annualAnticipatedInvestmentAmount: handleDataSelect(
				corporateMetadata?.annualAnticipatedInvestmentAmount ?? [],
			),
			investorTypes: handleDataSelect(corporateMetadata?.investorTypes ?? []),
		}),
		[corporateMetadata],
	);

	const individualMetadataOptions = useMemo(
		() => ({
			sourceOfWealth: handleDataSelect(individualMetadata?.sourceOfWealth ?? []),
			sourceOfFunds: handleDataSelect(individualMetadata?.sourceOfFunds ?? []),
			incomeLevel: handleDataSelect(individualMetadata?.incomeLevel ?? []),
			annualAnticipatedInvestmentAmount: handleDataSelect(
				individualMetadata?.annualAnticipatedInvestmentAmount ?? [],
			),
			investorTypes: handleDataSelect(individualMetadata?.investorTypes ?? []),
		}),
		[individualMetadata],
	);

	const isLoading =
		(isIndividualInvestor &&
			(isLoadingInvestorUserProfile ||
				isLoadingIndividualProfile ||
				isLoadingIndividualMetadata)) ||
		(isCorporateInvestor &&
			(isLoadingInvestorUserProfile ||
				isLoadingCorporateProgress ||
				isLoadingComplianceInfo ||
				isLoadingCorporateMetadata));

	const open = useCallback(() => {
		if (!isLoggedIn) return;
		setSessionDismissed(false);
		setIsOpen(true);
	}, [isLoggedIn]);

	const close = useCallback(() => {
		setIsOpen(false);
		setSessionDismissed(true);
		setPendingAction(null);
	}, []);

	const markCompleted = useCallback(() => {
		setIsOpen(false);
		setSessionDismissed(false);

		queryCache.invalidateQueries(INVESTOR_PROFILE_QUERY_KEY);

		if (isIndividualInvestor) {
			queryCache.invalidateQueries("/investors/investing-info");
		}

		if (isCorporateInvestor) {
			queryCache.invalidateQueries("/corporate-investors/compliance");
			queryCache.invalidateQueries("/corporate-investors/progress");
		}

		if (pendingAction) {
			pendingAction();
			setPendingAction(null);
		}
	}, [isCorporateInvestor, isIndividualInvestor, pendingAction, queryCache]);

	const guardAction = useCallback(
		(action?: () => void): boolean => {
			if (!isLoggedIn || !shouldRequireUpdate) {
				action?.();
				return true;
			}

			if (action) {
				setPendingAction(() => action);
			}

			open();
			return false;
		},
		[isLoggedIn, open, shouldRequireUpdate],
	);

	useEffect(() => {
		if (!isLoggedIn) {
			setIsOpen(false);
			setPendingAction(null);
			return;
		}

		if (!shouldRequireUpdate || sessionDismissed || isLoading) return;
		setIsOpen(true);
	}, [isLoggedIn, shouldRequireUpdate, sessionDismissed, isLoading]);

	return {
		accountType,
		isLoggedIn,
		isOpen,
		isLoading,
		shouldRequireUpdate,
		investorProfile,
		complianceInfo,
		individualInvestorClassifications,
		corporateInvestorClassifications,
		individualMetadataOptions,
		corporateMetadataOptions,
		open,
		close,
		markCompleted,
		guardAction,
		isIndividualInvestor,
		isCorporateInvestor,
	};
}

type InvestorClassificationRequirementContextValue = ReturnType<
	typeof useInvestorClassificationRequirementState
>;

const InvestorClassificationRequirementContext = createContext<
	InvestorClassificationRequirementContextValue | undefined
>(undefined);

export function InvestorClassificationRequirementProvider({ children }: PropsWithChildren) {
	const value = useInvestorClassificationRequirementState();

	return createElement(InvestorClassificationRequirementContext.Provider, { value }, children);
}

export function useInvestorClassificationRequirementContext() {
	const context = useContext(InvestorClassificationRequirementContext);

	if (context === undefined) {
		throw new Error(
			"useInvestorClassificationRequirementContext must be used within InvestorClassificationRequirementProvider",
		);
	}

	return context;
}
